import { getUserCoinPackages } from "../../app/helpers/coinPackages";
import { getCoinName, replaceCoinWord } from "../../app/helpers/helper";
import CoinIcon from "../../assets/icons/CoinIcon";
import CoinWalletSupportIcon from "../../assets/icons/CoinWalletSupportIcon";
import CopyIcon from "../../assets/icons/CopyIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import BalanceCard from "../../components/common/BalanceCard";
import CustomModal from "../../components/common/CustomModal/CustomModal";
import DynamicCoinPackIcon from "../../components/common/DynamicCoinPackIcon";
import Loader from "../../components/common/Loader";
import SeriesCoinModalContent from "../../components/common/SeriesCoinModalContent";
import TransactionList from "../../components/common/transactions/TransactionList";
import { TOAST_TYPES } from "../../app/constants";
import { useConfig } from "../../app/context/Config/configContext";
import { useNotification } from "../../app/context/Notification";
import gluedin from "gluedin-shorts-js";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const paymentModule = new gluedin.GluedInPaymentModule();
const rewardsModule = new gluedin.GluedInRewardsModule();
const userModule = new gluedin.GluedInUserModule();

const formatCouponDate = (value?: string | number | Date) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const normalizeNumber = (value: any) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const extractBalance = (response: any) => {
  const balance = Number(response?.data?.result?.balance);
  return Number.isFinite(balance) ? balance : 0;
};

const extractCouponList = (response: any) => {
  const list =
    response?.data?.result?.coupons ||
    response?.data?.result?.list ||
    response?.data?.result ||
    response?.data?.data ||
    [];
  return Array.isArray(list) ? list : [];
};

const getCouponSource = (coupon: any) => coupon?.couponsDetails || coupon;

const buildCouponMeta = (coupon: any) => {
  const source = getCouponSource(coupon);
  const title =
    source?.couponTitle || source?.localisedTitle?.en || "Coupon Title";
  const description =
    source?.description || source?.localisedDescription?.en || "";
  const coins = normalizeNumber(
    source?.coins ??
      source?.coinCost ??
      source?.requiredCoins ??
      source?.redeemptionPoints ??
      coupon?.points,
  );
  const validTill =
    source?.endDate ||
    source?.validTill ||
    source?.expiresAt ||
    source?.expiryDate ||
    "";
  const image = source?.couponImage || "";
  return {
    title,
    description,
    coins,
    validTill: formatCouponDate(validTill),
    image,
  };
};

const APP_STORE_URL =
  "https://apps.apple.com/in/app/gluedin-video-community/id1622464243";
const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.gluedin.app";

const CoinWallet = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { appConfig } = useConfig();
  const { showNotification } = useNotification();
  const defaultLanguage = localStorage.getItem("defaultLanguage");
  const coinPackIconUrl = appConfig?.appEconomy?.coinSettings?.coinUrl;
  const [coinBalance, setCoinBalance] = useState(0);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [myCoupons, setMyCoupons] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllCoupons, setShowAllCoupons] = useState(false);
  const [couponLoadingId, setCouponLoadingId] = useState<string | null>(null);
  const [showCoinPackModal, setShowCoinPackModal] = useState(false);
  const [showCoinModal, setShowCoinModal] = useState(false);
  const [coinPackages, setCoinPackages] = useState<any[]>([]);
  const [coinPackagesLoading, setCoinPackagesLoading] = useState(false);
  const [couponTab, setCouponTab] = useState<"available" | "mine">("available");
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<any | null>(null);
  const [couponModalCode, setCouponModalCode] = useState("");
  const [couponModalLoading, setCouponModalLoading] = useState(false);
  const [couponCodes, setCouponCodes] = useState<Record<string, string>>({});

  const resolveLabel = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const baseCoinsLabel = resolveLabel("label-coins", "Coins");
  const coinName = getCoinName(appConfig, baseCoinsLabel);
  const withCoinName = (value: string) => replaceCoinWord(value, coinName);
  const walletTitle = withCoinName(
    resolveLabel("label-coins-wallet", "Coins Wallet"),
  );
  const transactionsTitle = resolveLabel("label-transactions", "Transactions");
  const couponsTitle = resolveLabel("label-coupons", "Coupons");
  const availableCouponsLabel = resolveLabel(
    "label-available-coupons",
    "Available Coupons",
  );
  const myCouponsLabel = resolveLabel("label-my-coupons", "My Coupons");
  const viewAllLabel = resolveLabel("label-view-all", "View All");
  const availableCoinsLabel = withCoinName(
    resolveLabel("label-available-coins", "Available Coins"),
  );
  const balanceLabel = resolveLabel("label-balance", "Balance");
  const earnCoinsLabel = withCoinName(
    resolveLabel("label-earn-coins", "Earn Coins"),
  );
  const showEarnFromEngagementCta =
    appConfig?.appEconomy?.coinSettings?.earnFromEngagement?.enabled !== false;
  const addLabel = resolveLabel("label-add", "Add");
  const coinsLabel = withCoinName(baseCoinsLabel);
  const getCouponLabel = resolveLabel("label-get-coupon", "Get Coupon");
  const copyCodeLabel = resolveLabel("label-copy-code", "Copy Code");
  const redeemedLabel = resolveLabel("label-redeemed", "Redeemed");
  const howToUseLabel = resolveLabel("label-how-to-use", "How to Use");
  const validTillLabel = resolveLabel("label-valid-till", "Valid Till");
  const loadingLabel = resolveLabel("label-loading", "Loading");

  useEffect(() => {
    let isActive = true;
    const fetchBalance = async () => {
      try {
        const response = await userModule.getUserBalance();
        if (isActive) {
          setCoinBalance(extractBalance(response));
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchTransactions = async () => {
      try {
        const response = await paymentModule.getTransactionList({
          limit: 10,
          offset: 1,
        });
        const list =
          response?.data?.result?.transactions ||
          response?.data?.result?.list ||
          response?.data?.result ||
          response?.data?.data ||
          [];
        if (isActive) {
          setTransactions(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchCoupons = async () => {
      try {
        const response = await rewardsModule.getCouponsList({
          limit: 12,
          offset: 1,
        });
        if (isActive) {
          setCoupons(extractCouponList(response));
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchMyCoupons = async () => {
      try {
        const response = await rewardsModule.getMyCoupons({
          limit: 12,
          offset: 1,
        });
        if (isActive) {
          setMyCoupons(extractCouponList(response));
        }
      } catch (error) {
        console.error(error);
      }
    };

    const fetchCoinPackages = async () => {
      if (isActive) {
        setCoinPackagesLoading(true);
      }
      try {
        const packages = await getUserCoinPackages();
        if (isActive) {
          setCoinPackages(packages);
        }
      } catch (error) {
        console.error(error);
        if (isActive) {
          setCoinPackages([]);
        }
      } finally {
        if (isActive) {
          setCoinPackagesLoading(false);
        }
      }
    };

    const fetchAll = async () => {
      await Promise.all([
        fetchBalance(),
        fetchTransactions(),
        fetchCoupons(),
        fetchMyCoupons(),
        fetchCoinPackages(),
      ]);
      if (isActive) {
        setIsLoading(false);
      }
    };

    fetchAll();
    return () => {
      isActive = false;
    };
  }, []);

  const transactionList = useMemo(
    () => transactions.slice(0, 6),
    [transactions],
  );

  const couponList = useMemo(() => coupons.slice(0, 6), [coupons]);
  const activeCoupons = couponTab === "mine" ? myCoupons : coupons;

  useEffect(() => {
    if (showAllCoupons) {
      setCouponTab("available");
    }
  }, [showAllCoupons]);

  const resolveCouponId = (coupon: any) =>
    coupon?.couponId ||
    coupon?.couponsDetails?._id ||
    coupon?.id ||
    coupon?._id;

  const getCouponCodeValue = (coupon: any) =>
    coupon?.couponCode ||
    coupon?.code ||
    coupon?.coupon?.code ||
    coupon?.couponsDetails?.couponCode ||
    "";

  const parseHowToUseItems = (coupon: any) => {
    const source = getCouponSource(coupon);
    const raw = source?.howToUse || "";
    if (Array.isArray(raw)) {
      return raw.map((item) => String(item).trim()).filter(Boolean);
    }
    if (typeof raw !== "string") return [];
    return raw
      .split(/\r?\n/)
      .map((item) => item.replace(/^[\u2022\-*]\s*/, "").trim())
      .filter(Boolean);
  };

  const defaultHowToUse = [`Tap "${getCouponLabel}" and copy the code.`];

  const isCouponRedeemed = (coupon: any) =>
    coupon?.status === "REDEEMED" ||
    coupon?.status === "USED" ||
    coupon?.redeemed === true;

  const openCouponModal = (coupon: any) => {
    const couponId = resolveCouponId(coupon);
    const existingCode =
      (couponId && couponCodes[couponId]) || getCouponCodeValue(coupon);
    setSelectedCoupon(coupon);
    setCouponModalCode(existingCode || "");
    setCouponModalLoading(false);
    setShowCouponModal(true);
  };

  const closeCouponModal = () => {
    setShowCouponModal(false);
    setSelectedCoupon(null);
    setCouponModalCode("");
    setCouponModalLoading(false);
  };

  const storeCouponCode = (couponId: string | undefined, code: string) => {
    if (!couponId || !code) return;
    setCouponCodes((prev) => ({ ...prev, [couponId]: code }));
  };

  const copyToClipboard = async (text: string) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleGetCoupon = async (couponId?: string) => {
    if (!couponId) return;
    setCouponLoadingId(couponId);
    setCouponModalLoading(true);
    try {
      const buyResponse = await rewardsModule.buyCouponViaCoin(couponId);
      const buySuccess =
        buyResponse?.status === 200 ||
        buyResponse?.status === 201 ||
        buyResponse?.data?.success;

      const codeResponse = await rewardsModule.getCouponCode(couponId);
      const code =
        codeResponse?.data?.result?.code ||
        codeResponse?.data?.result?.couponCode ||
        codeResponse?.data?.code ||
        "";

      if (code) {
        setCouponModalCode(code);
        storeCouponCode(couponId, code);
      }

      if (!code && !buySuccess) {
        throw new Error("Unable to claim this coupon.");
      }

      const [balanceResponse, couponsResponse, myCouponsResponse] =
        await Promise.all([
          userModule.getUserBalance(),
          rewardsModule.getCouponsList({ limit: 12, offset: 1 }),
          rewardsModule.getMyCoupons({ limit: 12, offset: 1 }),
        ]);
      setCoinBalance(extractBalance(balanceResponse));
      setCoupons(extractCouponList(couponsResponse));
      setMyCoupons(extractCouponList(myCouponsResponse));
    } catch (error) {
      console.error(error);
      showNotification({
        title: resolveLabel("label-coupon-error", "Coupon Error"),
        subTitle: resolveLabel(
          "label-coupon-error-detail",
          "Unable to claim this coupon.",
        ),
        type: TOAST_TYPES.ERROR,
      });
    } finally {
      setCouponLoadingId(null);
      setCouponModalLoading(false);
    }
  };

  const handleCopyCouponCode = async (coupon: any) => {
    const couponId = resolveCouponId(coupon);
    if (!couponId) return;
    let code = couponCodes[couponId] || getCouponCodeValue(coupon);
    if (!code) {
      setCouponLoadingId(couponId);
      try {
        const codeResponse = await rewardsModule.getCouponCode(couponId);
        code =
          codeResponse?.data?.result?.code ||
          codeResponse?.data?.result?.couponCode ||
          codeResponse?.data?.code ||
          "";
        if (code) {
          storeCouponCode(couponId, code);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setCouponLoadingId(null);
      }
    }
    if (code) {
      await copyToClipboard(code);
    }
  };

  const handleOpenCoinPackModal = () => {
    setShowCoinPackModal(true);
  };

  const handleCloseCoinPackModal = () => {
    setShowCoinPackModal(false);
  };

  const handleCoinPackClick = () => {
    setShowCoinPackModal(false);
    setShowCoinModal(true);
  };

  const selectedMeta = selectedCoupon ? buildCouponMeta(selectedCoupon) : null;
  const selectedHowTo = selectedCoupon
    ? parseHowToUseItems(selectedCoupon)
    : [];
  const howToItems = selectedHowTo.length > 0 ? selectedHowTo : defaultHowToUse;
  const hasCouponCode = Boolean(couponModalCode);
  const selectedCouponId = selectedCoupon
    ? resolveCouponId(selectedCoupon)
    : undefined;

  return (
    <div className="full-sec"><div className="right-sec profile-content-list coin-wallet-page">
        <div className="full-box coin-wallet-full-box">
          <div
            className={`back-header coin-wallet-header ${
              showAllCoupons ? "coin-wallet-header--coupons" : ""
            }`}
          >
            <ul className="list list-inline">
              <li className="back-btn">
                <button
                  className="back-btn-a"
                  type="button"
                  onClick={() => {
                    if (showAllCoupons) {
                      setShowAllCoupons(false);
                      return;
                    }
                    navigate(-1);
                  }}
                >
                  {defaultLanguage === "en" ? (
                    <LeftArrowIcon />
                  ) : (
                    <RightArrowIcon />
                  )}
                  {t("back-btn")}
                </button>
              </li>
              <li className="common-heading">
                {showAllCoupons ? couponsTitle : walletTitle}
              </li>
              <li>
                <button
                  className="common-support"
                  onClick={() => navigate("/support")}
                >
                  <CoinWalletSupportIcon />
                </button>
              </li>
              {showAllCoupons && (
                <li className="coin-wallet-header__spacer" aria-hidden="true" />
              )}
            </ul>
          </div>

          {isLoading && <Loader />}

          {!isLoading && !showAllCoupons && (
            <div className="coin-wallet-content">
              <BalanceCard
                className="coin-wallet-card coin-wallet-balance"
                textClassName="coin-wallet-balance__text"
                addButtonClassName="coin-wallet-balance__add"
                label={availableCoinsLabel}
                balance={coinBalance}
                addLabel={addLabel}
                onAdd={handleOpenCoinPackModal}
              />

              <div className="coin-wallet-section">
                <div className="coin-wallet-section__header">
                  <h3>{transactionsTitle}</h3>
                  <button
                    type="button"
                    className="coin-wallet-section__view"
                    onClick={() => navigate("/coin-wallet/transactions")}
                  >
                    {viewAllLabel}
                  </button>
                </div>
                <TransactionList
                  transactions={transactionList}
                  emptyLabel={resolveLabel(
                    "label-no-transactions",
                    "No transactions yet.",
                  )}
                  showDateInMiddle={true}
                />
              </div>

              {showEarnFromEngagementCta && (
                <div className="coin-wallet-cta">
                  <div className="coin-wallet-cta__text">
                    {withCoinName(
                      resolveLabel(
                        "label-earn-coins-tip",
                        "Play, watch, and interact - every move earns you Coins",
                      ),
                    )}
                  </div>
                  <button
                    className="coin-wallet-cta__button"
                    type="button"
                    onClick={() => navigate("/earn-coins")}
                  >
                    {earnCoinsLabel}
                  </button>
                </div>
              )}

              <div className="coin-wallet-section">
                <div className="coin-wallet-section__header">
                  <h3>{couponsTitle}</h3>
                  <button
                    type="button"
                    className="coin-wallet-section__view"
                    onClick={() => setShowAllCoupons(true)}
                  >
                    {viewAllLabel}
                  </button>
                </div>
                <div className="coin-wallet-coupons">
                  {couponList?.length === 0 && (
                    <div className="coin-wallet-empty">
                      {resolveLabel(
                        "label-no-coupons",
                        "No coupons available.",
                      )}
                    </div>
                  )}
                  {couponList?.map((coupon) => {
                    const meta = buildCouponMeta(coupon);
                    const couponId =
                      coupon?.couponId ||
                      coupon?.couponsDetails?._id ||
                      coupon?.id ||
                      coupon?._id;
                    return (
                      <div
                        key={couponId || meta.title}
                        className="coin-wallet-coupon"
                      >
                        <div className="coin-wallet-coupon__image">
                          {meta.image ? (
                            <img src={meta.image} alt={meta.title} />
                          ) : (
                            <span>{meta.title.slice(0, 1).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="coin-wallet-coupon__body">
                          <h4>{meta.title}</h4>
                          {meta.description && <p>{meta.description}</p>}
                          <div className="coin-wallet-coupon__coins">
                            {meta.coins} {coinsLabel}
                          </div>
                        </div>
                        {meta.validTill && (
                          <div className="coin-wallet-coupon__valid">
                            {validTillLabel} : {meta.validTill}
                          </div>
                        )}
                        <button
                          className="coin-wallet-coupon__button"
                          type="button"
                          onClick={() => openCouponModal(coupon)}
                        >
                          {getCouponLabel}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!isLoading && showAllCoupons && (
            <div className="coin-wallet-coupons-page">
              <div className="coin-wallet-coupons-tabs">
                <button
                  type="button"
                  className={`coin-wallet-coupons-tab ${
                    couponTab === "available"
                      ? "coin-wallet-coupons-tab--active"
                      : ""
                  }`}
                  onClick={() => setCouponTab("available")}
                >
                  {availableCouponsLabel}
                </button>
                <button
                  type="button"
                  className={`coin-wallet-coupons-tab ${
                    couponTab === "mine"
                      ? "coin-wallet-coupons-tab--active"
                      : ""
                  }`}
                  onClick={() => setCouponTab("mine")}
                >
                  {myCouponsLabel}
                </button>
              </div>

              <div className="coin-wallet-coupons-grid">
                {activeCoupons.length === 0 && (
                  <div className="coin-wallet-empty">
                    {resolveLabel(
                      couponTab === "mine"
                        ? "label-no-coupons-in-wallet"
                        : "label-no-coupons",
                      couponTab === "mine"
                        ? "No coupons in your wallet yet."
                        : "No coupons available.",
                    )}
                  </div>
                )}
                {activeCoupons.map((coupon) => {
                  const meta = buildCouponMeta(coupon);
                  const couponId =
                    coupon?.couponId ||
                    coupon?.couponsDetails?._id ||
                    coupon?.id ||
                    coupon?._id;
                  const isLoadingCoupon = couponLoadingId === couponId;
                  const isMineTab = couponTab === "mine";
                  const isRedeemed = isMineTab && isCouponRedeemed(coupon);
                  const buttonLabel = isMineTab
                    ? isRedeemed
                      ? redeemedLabel
                      : copyCodeLabel
                    : getCouponLabel;
                  const buttonClass = `coin-wallet-coupon__button ${
                    isMineTab ? "coin-wallet-coupon__button--outline" : ""
                  } ${isRedeemed ? "coin-wallet-coupon__button--muted" : ""}`;
                  return (
                    <div
                      key={couponId || meta.title}
                      className="coin-wallet-coupon coin-wallet-coupon--page"
                    >
                      <div className="coin-wallet-coupon__image coin-wallet-coupon__image--page">
                        {meta.image ? (
                          <img src={meta.image} alt={meta.title} />
                        ) : (
                          <span>{meta.title.slice(0, 1).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="coin-wallet-coupon__body">
                        <h4>{meta.title}</h4>
                        {meta.description && <p>{meta.description}</p>}
                        <div className="coin-wallet-coupon__coins">
                          {meta.coins} {coinsLabel}
                        </div>
                      </div>
                      {meta.validTill && (
                        <div className="coin-wallet-coupon__valid">
                          {validTillLabel} : {meta.validTill}
                        </div>
                      )}
                      <button
                        className={buttonClass}
                        type="button"
                        disabled={isLoadingCoupon || isRedeemed}
                        onClick={() => {
                          if (isMineTab) {
                            handleCopyCouponCode(coupon);
                            return;
                          }
                          openCouponModal(coupon);
                        }}
                      >
                        {isLoadingCoupon ? loadingLabel : buttonLabel}
                        {isMineTab && !isRedeemed && (
                          <span
                            className="coin-wallet-coupon__copy-icon"
                            aria-hidden="true"
                          >
                            <CopyIcon />
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCouponModal && selectedMeta && (
        <CustomModal
          isOpen={showCouponModal}
          close={closeCouponModal}
          classes={"coin-wallet-coupon-modal"}
        >
          <div className="coin-wallet-coupon-modal__content">
            <div className="coin-wallet-coupon-modal__image">
              {selectedMeta.image ? (
                <img src={selectedMeta.image} alt={selectedMeta.title} />
              ) : (
                <span>{selectedMeta.title.slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="coin-wallet-coupon-modal__details">
              <h3>{selectedMeta.title}</h3>
              {selectedMeta.description && <p>{selectedMeta.description}</p>}
              <div className="coin-wallet-coupon-modal__coins">
                {selectedMeta.coins} {coinsLabel}
              </div>
              {selectedMeta.validTill && (
                <div className="coin-wallet-coupon-modal__valid">
                  {validTillLabel} : {selectedMeta.validTill}
                </div>
              )}
            </div>

            {hasCouponCode ? (
              <button
                type="button"
                className="coin-wallet-coupon-modal__code"
                onClick={() => copyToClipboard(couponModalCode)}
              >
                <span>{couponModalCode}</span>
                <span className="coin-wallet-coupon-modal__copy-icon">
                  <CopyIcon />
                </span>
              </button>
            ) : (
              <button
                type="button"
                className="coin-wallet-coupon-modal__action"
                disabled={couponModalLoading || !selectedCouponId}
                onClick={() => handleGetCoupon(selectedCouponId)}
              >
                {couponModalLoading ? loadingLabel : getCouponLabel}
              </button>
            )}

            {howToItems.length > 0 && (
              <div className="coin-wallet-coupon-modal__howto">
                <h4>{howToUseLabel}</h4>
                <ul>
                  {howToItems.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CustomModal>
      )}

      {showCoinPackModal && (
        <CustomModal
          isOpen={showCoinPackModal}
          close={handleCloseCoinPackModal}
          classes={"coin-wallet-unlock-modal"}
        >
          <div className="series-unlock series-unlock--open coin-wallet-unlock">
            <div className="coin-wallet-unlock__balance">
              <span>{balanceLabel}:</span>
              <CoinIcon className="gi-coin-icon gi-coin-small" aria-hidden />
              <span>{coinBalance}</span>
            </div>
            <div className="series-packs__container">
              <h4>Buy Coins</h4>
              <div className="series-unlock__packs">
                {coinPackagesLoading && (
                  <p className="series-unlock__subtitle">{loadingLabel}...</p>
                )}
                {!coinPackagesLoading &&
                  coinPackages?.map((pack) => (
                    <button
                      key={pack.id}
                      type="button"
                      className="series-unlock__pack"
                      onClick={handleCoinPackClick}
                    >
                      <DynamicCoinPackIcon coinUrl={coinPackIconUrl} />
                      <span className="series-unlock__pack-text">
                        <span className="series-unlock__pack-amount">
                          {pack.coins} {coinsLabel}
                        </span>
                        <span className="series-unlock__pack-price series-unlock__pack-price--blur">
                          {pack.priceLabel ||
                            (Number.isFinite(Number(pack.price))
                              ? `$${Number(pack.price).toFixed(2)}`
                              : "")}
                        </span>
                      </span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </CustomModal>
      )}

      {showCoinModal && (
        <CustomModal
          isOpen={showCoinModal}
          close={() => setShowCoinModal(false)}
          classes={"series-coin-modal"}
        >
          <SeriesCoinModalContent
            title={t("label-download-our-app")}
            subtitle={t("label-download-our-app-subtitle")}
            detail={t("label-feature-available-app-detail")}
            appStoreUrl={APP_STORE_URL}
            googlePlayUrl={GOOGLE_PLAY_URL}
          />
        </CustomModal>
      )}
    </div>
  );
};

export default CoinWallet;
